import React, { useEffect, useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Fade,
  Grid,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from "@mui/material";
import {
  CheckCircleOutline,
  ErrorOutline,
  PendingActions,
  ArrowBack,
  Campaign,
  Link,
  Close,
} from "@mui/icons-material";
import PageTitle from "../../widgets/PageTitle";
import BackBtn from "../../widgets/BackBtn";
import TemplatePreview from "../../widgets/TemplatePreview";
import { GlassButton } from "../../themes/UiElements";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { toast } from "react-toastify";
import { CheckCircle, HelpCircle, XCircle } from "lucide-react";

const statusConfig = {
  APPROVED: { icon: CheckCircleOutline, color: "success", label: "Approved" },
  REJECTED: { icon: ErrorOutline, color: "error", label: "Rejected" },
  PENDING: { icon: PendingActions, color: "warning", label: "Pending" },
};

const TemplateStatusChecker = () => {
  const { id } = useParams();
  const [linking, setLinking] = useState(false);
  const navigate = useNavigate();
  const [rcsDialogOpen, setRcsDialogOpen] = useState(false);
  const [rcsTemplateId, setRcsTemplateId] = useState("");
  const {
    data: template,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/api/get-template/${id}`);

  useEffect(() => {
    if (template?.status === "PENDING") {
      const interval = setInterval(refetch, 30000);
      return () => clearInterval(interval);
    }
  }, [template?.status, refetch]);

  const isSaved = template?.isSavedDb;

  const savedConfig =
    isSaved === true
      ? {
          label: "Saved",
          color: "success",
          icon: CheckCircle,
        }
      : isSaved === false
      ? {
          label: "Not Saved",
          color: "error",
          icon: XCircle,
        }
      : {
          label: "Unknown",
          color: "default",
          icon: HelpCircle,
        };

  const formatToIST = (utcTime) => {
    try {
      const date = new Date(utcTime);
      return new Date(date.getTime() + 5.5 * 60 * 60 * 1000).toLocaleString(
        "en-IN",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }
      );
    } catch (e) {
      return utcTime;
    }
  };

  const handleOpenRcsDialog = () => {
    setRcsDialogOpen(true);
  };

  const handleCloseRcsDialog = () => {
    setRcsDialogOpen(false);
    setRcsTemplateId("");
  };

  const handleSubmitRcsTemplate = async () => {
    if (!rcsTemplateId.trim()) {
      // Handle validation - you might want to show an error
      return;
    }

    try {
      setLinking(true);
      const response = await axios.post(
        `${API_ENDPOINT}/api/link-rcs-template-to-waba/${id}`,
        { rcsTemplateId },
        getHeader()
      );
      toast.success(response.data.message);
    } catch (error) {
      toastError(error);
    } finally {
      setLinking(false);
    }
    handleCloseRcsDialog();
  };

  if (isLoading && !template)
    return <CircularProgress sx={{ mx: "auto", mt: 4, display: "block" }} />;
  if (isError)
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error: {error.message}
      </Alert>
    );
  if (!template)
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        Template not found
      </Alert>
    );

  const { status } = template;
  const StatusIcon = statusConfig[status]?.icon;

  return (
    <Container sx={{ mt: 3 }}>
      <BackBtn />
      <PageTitle>Template Details</PageTitle>
      {status === "PENDING" ? (
        <Box textAlign="center" mt={4}>
          <Fade in={true} timeout={1000}>
            <Box>
              <CircularProgress
                size={80}
                thickness={4}
                sx={{
                  mb: 3,
                  color: (theme) => theme.palette.warning.main,
                  animationDuration: "1.5s",
                }}
              />
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
                {template.name}
              </Typography>
              <Chip
                icon={<StatusIcon color={statusConfig[status]?.color} />}
                label={statusConfig[status]?.label}
                color={statusConfig[status]?.color}
                variant="outlined"
                sx={{ mb: 3 }}
              />
              <Typography variant="body1" color="text.secondary">
                Your template is being reviewed
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                We'll notify you when it's ready. This may take a few minutes.
              </Typography>
            </Box>
          </Fade>
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 2, mb: 3, mt: 1 }}>
              {[
                { label: "Name", value: template.name, variant: "h6" },
                { label: "ID", value: template.id },
                { label: "Category", value: template.category },
                {
                  label: "Status",
                  component: (
                    <Chip
                      icon={<StatusIcon color={statusConfig[status]?.color} />}
                      label={statusConfig[status]?.label}
                      color={statusConfig[status]?.color}
                      variant="outlined"
                    />
                  ),
                },
                {
                  label: "Is Saved?",
                  component: (
                    <Chip
                      icon={<savedConfig.icon size={16} />}
                      label={savedConfig.label}
                      color={savedConfig.color}
                      variant="outlined"
                    />
                  ),
                },
                { label: "Language", value: template.language },
                {
                  label: "Last Updated",
                  value: `${formatToIST(template.last_updated_time)} (IST)`,
                },
              ].map((item, i) => (
                <Box key={i}>
                  {i > 0 && <Divider sx={{ my: 1.5 }} />}
                  <Typography variant="subtitle2" color="text.secondary">
                    {item.label}
                  </Typography>
                  {item.component || (
                    <Typography variant={item.variant || "body1"}>
                      {item.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Paper>

            {/* Connect RCS Template Button - Shows for approved templates */}
            {status === "APPROVED" && (
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Link />}
                  onClick={handleOpenRcsDialog}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  Connect RCS Template for Failed Messages
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Link an RCS template to send when messages fail to deliver
                </Typography>
              </Box>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={() => navigate("/home/templates")}
              >
                Template List
              </Button>

              {status === "APPROVED" && (
                <Button
                  variant="contained"
                  startIcon={<Campaign />}
                  onClick={() => navigate("/home/campaign")}
                >
                  Start Campaign
                </Button>
              )}
            </Stack>

            {status === "REJECTED" && (
              <Alert severity="error" sx={{ mt: 3 }}>
                <Typography variant="subtitle2">Rejection Reason:</Typography>
                {template.rejected_reason !== "NONE"
                  ? template.rejected_reason
                  : "No reason provided"}
              </Alert>
            )}
          </Grid>
          <Grid item xs={6}>
            <TemplatePreview template={template} previewUrl={""} />
          </Grid>
        </Grid>
      )}

      {/* RCS Template Connection Dialog */}
      <Dialog
        open={rcsDialogOpen}
        onClose={handleCloseRcsDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Connect RCS Template for Failed Messages
          <IconButton
            aria-label="close"
            onClick={handleCloseRcsDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label="RCS Template ID"
              value={rcsTemplateId}
              onChange={(e) => setRcsTemplateId(e.target.value)}
              placeholder="Enter RCS template ID"
              variant="outlined"
              sx={{ mb: 2 }}
            />
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Enter the ID of the RCS template you want to link for failed
              messages.
            </Typography>
            <a href="/home/rcs-templates" target="_blank">
              <GlassButton
                variant="text"
                color="primary"
                size="small"
                sx={{ mt: 1 }}
              >
                View RCS Templates List
              </GlassButton>
            </a>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRcsDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitRcsTemplate}
            variant="contained"
            disabled={!rcsTemplateId.trim()}
          >
            Link Template
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplateStatusChecker;
